import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ChevronLeft, ChevronRight, ListOrdered, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { CallDetailSheet } from "@/components/call-detail-sheet";
import { ResultBadge } from "@/components/badges";
import { useDayCalls } from "@/hooks/use-day-calls";
import { todayLocalDate } from "@/hooks/use-intake";
import { flattenCasesToCallRows } from "@/lib/data-transform";
import { formatDuration, formatEnumLabel, formatPersonName } from "@/lib/formatters";
import config from "@/config";
import { cn } from "@/utils";
import type { CallRow, CaseRecord, DayCall } from "@/types";

const TZ = "America/New_York";

const timeFmt = new Intl.DateTimeFormat("en-US", {
  timeZone: TZ,
  hour: "numeric",
  minute: "2-digit",
});

type CallFilter = "all" | "result" | "failed";
type SortOrder = "asc" | "desc";

const FILTERS: { id: CallFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "result", label: "Results" },
  { id: "failed", label: "Failed" },
];

type OpenDayCalls = (date?: string) => void;

const DayCallsContext = createContext<OpenDayCalls>(() => {});

export function useOpenDayCalls(): OpenDayCalls {
  return useContext(DayCallsContext);
}

function shiftIsoDate(ymd: string, days: number): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!match) return ymd;
  const dt = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  dt.setUTCDate(dt.getUTCDate() + days);
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const d = String(dt.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function labelForIsoDate(ymd: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!match) return ymd;
  const dt = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12),
  );
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(dt);
}

function formatEtTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return timeFmt.format(d);
}

function outcomeLabel(call: DayCall): string {
  if (call.success === "true") {
    return call.paStatus ? formatEnumLabel(call.paStatus) : "Result";
  }
  if (call.success === "false") {
    return call.failReason ? formatEnumLabel(call.failReason) : "Failed";
  }
  return "In progress";
}

function rowKey(call: DayCall): string {
  return `${call.caseId}__${call.attemptIndex}__${call.callId}`;
}

function DayCallsDrawer({
  open,
  onOpenChange,
  requestedDate,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requestedDate: string | null;
}) {
  const today = useMemo(() => todayLocalDate(TZ), []);
  const [date, setDate] = useState(today);
  const [filter, setFilter] = useState<CallFilter>("all");
  const [sort, setSort] = useState<SortOrder>("asc");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<CallRow | null>(null);
  const [openingKey, setOpeningKey] = useState<string | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const openAbortRef = useRef<AbortController | null>(null);
  const sortTouched = useRef(false);

  useEffect(() => {
    if (open && requestedDate) setDate(requestedDate);
  }, [open, requestedDate]);

  const { calls, loading, error, truncated, refresh } = useDayCalls(date, open);

  useEffect(() => {
    if (loading) return;
    const el = listRef.current;
    if (!el) return;
    if (sortTouched.current) {
      sortTouched.current = false;
      el.scrollTop = 0;
      return;
    }
    el.scrollTop = sort === "asc" && date === today ? el.scrollHeight : 0;
  }, [loading, calls, date, today, sort]);

  const counts = useMemo(() => {
    let results = 0;
    let failed = 0;
    for (const call of calls) {
      if (call.success === "true") results += 1;
      else if (call.success === "false") failed += 1;
    }
    return { results, failed };
  }, [calls]);

  const chooseSort = useCallback((next: SortOrder) => {
    sortTouched.current = true;
    setSort(next);
  }, []);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = calls.filter((call) => {
      if (filter === "result" && call.success !== "true") return false;
      if (filter === "failed" && call.success !== "false") return false;
      if (!term) return true;
      const name = formatPersonName(call.memberFirstName, call.memberLastName).toLowerCase();
      const haystack = [
        call.caseId,
        call.callId,
        name,
        call.productName,
        call.payerName,
        call.paStatus,
        call.failReason,
        outcomeLabel(call),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
    if (sort === "desc") return filtered.slice().reverse();
    return filtered;
  }, [calls, filter, search, sort]);

  const setClampedDate = useCallback(
    (next: string) => {
      if (!next) return;
      setDate(next > today ? today : next);
    },
    [today],
  );

  const openCall = useCallback(async (call: DayCall) => {
    openAbortRef.current?.abort();
    const ac = new AbortController();
    openAbortRef.current = ac;
    const key = rowKey(call);
    setOpeningKey(key);
    setOpenError(null);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/cases/${encodeURIComponent(call.caseId)}`,
        { signal: ac.signal },
      );
      if (!res.ok) throw new Error(`Could not open this call (${res.status})`);
      const data = (await res.json()) as { cases?: Record<string, unknown>[] };
      const raw = data.cases?.[0];
      if (!raw) throw new Error("Case not found");
      const mapped = {
        ...raw,
        id: String(raw.document_id || raw.id || call.caseId),
      } as CaseRecord;
      const rows = flattenCasesToCallRows([mapped]);
      const match =
        rows.find((row) => row.callId && row.callId === call.callId) ??
        rows.find((row) => row.attemptIndex === call.attemptIndex) ??
        null;
      if (!match) throw new Error("That attempt is no longer on the case");
      setSelected(match);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setOpenError(err instanceof Error ? err.message : "Could not open this call");
    } finally {
      if (openAbortRef.current === ac) setOpeningKey(null);
    }
  }, []);

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          className="flex h-full w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[36rem]"
          onPointerDownOutside={(event) => {
            if (selected) event.preventDefault();
          }}
          onInteractOutside={(event) => {
            if (selected) event.preventDefault();
          }}
          onFocusOutside={(event) => {
            if (selected) event.preventDefault();
          }}
        >
          <SheetHeader className="space-y-3 border-b px-4 py-4 pr-12 text-left">
            <div>
              <SheetTitle className="flex items-center gap-2">
                <ListOrdered className="h-4 w-4" />
                Day's calls
              </SheetTitle>
              <SheetDescription>
                Every graded call that day. Times are Eastern.
              </SheetDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0"
                aria-label="Previous day"
                onClick={() => setClampedDate(shiftIsoDate(date, -1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-0 flex-1">
                <div className="mb-1 text-center text-sm font-medium">
                  {labelForIsoDate(date)}
                  {date === today ? " · Today" : ""}
                </div>
                <Input
                  type="date"
                  value={date}
                  max={today}
                  aria-label="Choose a day"
                  className="h-9"
                  onChange={(e) => setClampedDate(e.target.value)}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0"
                aria-label="Next day"
                disabled={date >= today}
                onClick={() => setClampedDate(shiftIsoDate(date, 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                {loading
                  ? "Loading…"
                  : `${calls.length} call${calls.length === 1 ? "" : "s"} · ${counts.results} result${counts.results === 1 ? "" : "s"}`}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant={sort === "asc" ? "default" : "outline"}
                  className="h-7 px-2"
                  aria-pressed={sort === "asc"}
                  onClick={() => chooseSort("asc")}
                >
                  Earliest
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={sort === "desc" ? "default" : "outline"}
                  className="h-7 px-2"
                  aria-pressed={sort === "desc"}
                  onClick={() => chooseSort("desc")}
                >
                  Latest
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2"
                  disabled={date === today}
                  onClick={() => setDate(today)}
                >
                  Today
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {FILTERS.map((item) => (
                <Button
                  key={item.id}
                  type="button"
                  size="sm"
                  variant={filter === item.id ? "default" : "outline"}
                  className="h-7 px-2.5"
                  onClick={() => setFilter(item.id)}
                >
                  {item.label}
                  {item.id === "result" ? ` ${counts.results}` : ""}
                  {item.id === "failed" ? ` ${counts.failed}` : ""}
                  {item.id === "all" ? ` ${calls.length}` : ""}
                </Button>
              ))}
            </div>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search case, patient, product, result…"
              aria-label="Filter calls"
              className="h-9"
            />
            {error ? (
              <p className="text-xs text-destructive" role="alert">
                {error}{" "}
                <button type="button" className="underline" onClick={() => void refresh()}>
                  Retry
                </button>
              </p>
            ) : null}
            {openError ? (
              <p className="text-xs text-destructive" role="alert">
                {openError}
              </p>
            ) : null}
            {truncated ? (
              <p className="text-xs text-amber-700">
                This day was too large to load in one pass. Some calls may be missing.
              </p>
            ) : null}
          </SheetHeader>

          <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto">
            {loading && calls.length === 0 ? (
              <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading calls…
              </div>
            ) : visible.length === 0 ? (
              <p className="px-4 py-16 text-center text-sm text-muted-foreground">
                {calls.length === 0 ? "No calls on this day." : "No calls match this filter."}
              </p>
            ) : (
              <ul>
                {visible.map((call) => {
                  const key = rowKey(call);
                  const name = formatPersonName(call.memberFirstName, call.memberLastName);
                  const opening = openingKey === key;
                  return (
                    <li key={key}>
                      <button
                        type="button"
                        onClick={() => void openCall(call)}
                        className={cn(
                          "flex w-full flex-col gap-1 border-b px-4 py-3 text-left hover:bg-accent",
                          opening && "bg-accent",
                        )}
                      >
                        <span className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium tabular-nums">
                            {formatEtTime(call.timestamp)}
                          </span>
                          <span className="flex items-center gap-2">
                            {opening ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                            <ResultBadge success={call.success} />
                            <span className="text-xs tabular-nums text-muted-foreground">
                              {formatDuration(call.duration)}
                            </span>
                          </span>
                        </span>
                        <span className="text-sm">{outcomeLabel(call)}</span>
                        <span className="truncate text-xs text-muted-foreground">
                          {[name, call.productName, call.payerName].filter(Boolean).join(" · ") ||
                            "—"}
                          {call.totalAttempts > 1
                            ? ` · attempt ${call.attemptIndex + 1}/${call.totalAttempts}`
                            : ""}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </SheetContent>
      </Sheet>
      <CallDetailSheet callRow={selected} onClose={() => setSelected(null)} />
    </>
  );
}

export function DayCallsProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [requestedDate, setRequestedDate] = useState<string | null>(null);
  const openDayCalls = useCallback((date?: string) => {
    setRequestedDate(date && date.trim() ? date.trim() : null);
    setOpen(true);
  }, []);

  return (
    <DayCallsContext.Provider value={openDayCalls}>
      {children}
      <DayCallsDrawer open={open} onOpenChange={setOpen} requestedDate={requestedDate} />
    </DayCallsContext.Provider>
  );
}

export function OpenDayCallsButton({
  date,
  className,
}: {
  date?: string;
  className?: string;
}) {
  const openDayCalls = useOpenDayCalls();
  return (
    <Button
      type="button"
      variant="outline"
      className={className}
      onClick={() => openDayCalls(date)}
    >
      <ListOrdered className="h-4 w-4" />
      Day's calls
    </Button>
  );
}
